<template>
  <v-container fluid class="pa-0">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <div>
          <div class="text-h6">Monthly Fee Projection</div>
          <div class="subtitle-2 grey--text">
            {{ currentMonthLabel }} &middot; {{ userName }}
          </div>
        </div>
        <v-chip color="primary" class="font-weight-medium" outlined>
          Actual: {{ formatCurrency(currentActual) }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="error" type="error" dense outlined class="mb-4">
          {{ error }}
        </v-alert>
        <v-form ref="form" v-model="formValid" lazy-validation>
          <v-row align="start">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.low_estimate"
                label="Low estimate"
                type="number"
                prefix="$"
                :rules="numberRules"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.high_estimate"
                label="High estimate"
                type="number"
                prefix="$"
                :rules="numberRules.concat(highAtLeastLowRule)"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-end pt-4">
              <div class="d-flex align-center">
                <v-btn
                  color="primary"
                  class="mr-2"
                  :loading="saving"
                  :disabled="!formValid || saving"
                  @click="saveProjection"
                >
                  Save Projection
                </v-btn>
                <v-btn text @click="resetForm" :disabled="saving">Reset</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
        <v-row class="mt-2">
          <v-col cols="12" md="4">
            <div class="projection-stat">
              <div class="caption grey--text">Current range</div>
              <div class="text-h6">
                {{ formatCurrency(form.low_estimate) }} -
                {{ formatCurrency(form.high_estimate) }}
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="projection-stat">
              <div class="caption grey--text">Actual to date</div>
              <div class="text-h6">{{ formatCurrency(currentActual) }}</div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="projection-stat">
              <div class="caption grey--text">Delta vs. high</div>
              <div
                class="text-h6"
                :class="deltaClass(currentActual - (form.high_estimate || 0))"
              >
                {{ formatCurrency(currentActual - (form.high_estimate || 0)) }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mt-4">
      <v-card-title class="pb-0">
        <div>
          <div class="text-h6">Past Projections</div>
          <div class="subtitle-2 grey--text">Last 12 months</div>
        </div>
      </v-card-title>
      <v-data-table
        :headers="tableHeaders"
        :items="projectionRows"
        dense
        disable-sort
        class="elevation-0"
        :loading="loading"
      >
        <template v-slot:item.month="{ item }">
          {{ item.monthLabel }}
        </template>
        <template v-slot:item.low="{ item }">
          {{ formatCurrency(item.low) }}
        </template>
        <template v-slot:item.high="{ item }">
          {{ formatCurrency(item.high) }}
        </template>
        <template v-slot:item.actual="{ item }">
          {{ formatCurrency(item.actual) }}
        </template>
        <template v-slot:item.delta="{ item }">
          <span :class="deltaClass(item.delta)">
            {{ formatCurrency(item.delta) }}
          </span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import { hasRole } from "../util";
import FeeProjectionsQuery from "@/graphql/FeeProjections.gql";
import UpsertFeeProjection from "@/graphql/UpsertFeeProjection.gql";
import FeesByAttorneyByMonth from "@/graphql/FeesByAttorneyByMonth.gql";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

export default {
  name: "DashFeeProjection",
  data() {
    return {
      loading: false,
      saving: false,
      error: "",
      projections: [],
      fees: [],
      formValid: true,
      form: {
        low_estimate: null,
        high_estimate: null,
      },
      selectedUserName: "",
      selectedStaffCodes: [],
      numberRules: [
        (v) => (v !== null && v !== "") || "Required",
        (v) =>
          v === null || v === "" || Number(v) >= 0 || "Must be zero or more",
      ],
    };
  },
  computed: {
    ...mapGetters(["imitatedUser", "currentUser"]),
    selectedUserId() {
      if (
        this.currentUser &&
        hasRole(this.currentUser, "admin") &&
        this.imitatedUser
      ) {
        return this.imitatedUser.toString();
      }
      return (
        this.$route.params.id || (this.currentUser ? this.currentUser.id : null)
      );
    },
    userName() {
      return this.selectedUserName;
    },
    currentMonthStart() {
      return moment().startOf("month");
    },
    currentMonthLabel() {
      return this.currentMonthStart.format("MMMM YYYY");
    },
    feesByMonth() {
      if (!this.selectedStaffCodes.length) return {};
      const staffSet = new Set(
        this.selectedStaffCodes
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean)
      );

      return this.fees.reduce((acc, fee) => {
        // Attorney field may include extra spaces; normalize and support grouped codes
        const feeCodes = (fee.attorney || "")
          .toUpperCase()
          .split(/[^A-Z0-9]+/)
          .map((c) => c.trim())
          .filter((c) => c.length > 0);

        const matches = feeCodes.some((code) => staffSet.has(code));
        if (!matches) return acc;

        const key = moment(fee.month_start).format("YYYY-MM");
        const amt = Number(fee.total || 0);
        acc[key] = (acc[key] || 0) + amt;
        return acc;
      }, {});
    },
    currentActual() {
      const key = this.currentMonthStart.format("YYYY-MM");
      return this.feesByMonth[key] || 0;
    },
    projectionRows() {
      return this.projections
        .slice()
        .sort((a, b) => moment(b.month).valueOf() - moment(a.month).valueOf())
        .map((p) => {
          const key = moment(p.month).format("YYYY-MM");
          const actual = this.feesByMonth[key] || 0;
          const delta = actual - Number(p.high_estimate || 0);
          return {
            id: p.id,
            monthLabel: moment(p.month).format("MMM YYYY"),
            monthKey: key,
            low: Number(p.low_estimate || 0),
            high: Number(p.high_estimate || 0),
            actual,
            delta,
          };
        });
    },
    tableHeaders() {
      return [
        { text: "Month", value: "month" },
        { text: "Low", value: "low" },
        { text: "High", value: "high" },
        { text: "Actual", value: "actual" },
        { text: "Delta (Actual - High)", value: "delta" },
      ];
    },
    highAtLeastLowRule() {
      return (v) =>
        v === null ||
        v === "" ||
        Number(v) >= Number(this.form.low_estimate || 0) ||
        "High must be >= Low";
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.fetchData();
      },
    },
    imitatedUser() {
      this.fetchData();
    },
  },
  methods: {
    formatCurrency(val) {
      const num = Number(val || 0);
      return currency.format(num);
    },
    deltaClass(value) {
      if (value > 0) return "success--text";
      if (value < 0) return "error--text";
      return "";
    },
    resetForm() {
      const current = this.projections.find((p) =>
        moment(p.month).isSame(this.currentMonthStart, "month")
      );
      this.form.low_estimate = current ? current.low_estimate : null;
      this.form.high_estimate = current ? current.high_estimate : null;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    async fetchData() {
      if (!this.selectedUserId) return;
      this.loading = true;
      this.error = "";
      try {
        await this.setSelectedUserName();
        const [projectionRes, feesRes] = await Promise.all([
          this.$apollo.query({
            query: FeeProjectionsQuery,
            variables: {
              userId: this.selectedUserId,
            },
            fetchPolicy: "network-only",
          }),
          this.$apollo.query({
            query: FeesByAttorneyByMonth,
            variables: {
              startDate: moment()
                .subtract(12, "months")
                .startOf("month")
                .format("YYYY-MM-DD"),
              endDate: moment()
                .endOf("month")
                .format("YYYY-MM-DD"),
            },
            fetchPolicy: "network-only",
          }),
        ]);

        this.projections = projectionRes.data.attorney_fee_projections || [];
        this.fees = feesRes.data.fees_in_period_by_attorney_by_month || [];
        this.resetForm();
      } catch (err) {
        this.error = err.message || "Unable to load projections.";
      } finally {
        this.loading = false;
      }
    },
    async setSelectedUserName() {
      const selectedIsCurrent =
        this.currentUser &&
        String(this.currentUser.id) === String(this.selectedUserId);

      // If current user matches selected but staff code is missing, fetch it.
      if (selectedIsCurrent && this.currentUser?.staff_code) {
        this.selectedUserName = `${this.currentUser.first_name} ${this.currentUser.last_name}`;
        this.selectedStaffCodes = this.normalizeAndExpandStaffCodes(
          this.currentUser.staff_code
        );
        return;
      }

      const { data } = await this.$apollo.query({
        query: gql`
          query($id: ID!) {
            user(id: $id) {
              id
              first_name
              last_name
              staff_code
              __typename
            }
          }
        `,
        variables: { id: this.selectedUserId },
        fetchPolicy: "no-cache",
      });

      if (data && data.user) {
        this.selectedUserName = `${data.user.first_name} ${data.user.last_name}`;
        this.selectedStaffCodes = this.normalizeAndExpandStaffCodes(
          data.user.staff_code
        );
      } else {
        this.selectedUserName = "";
        this.selectedStaffCodes = [];
      }
    },
    normalizeAndExpandStaffCodes(staffCode) {
      if (!staffCode) return [];
      const tokens = staffCode
        .toUpperCase()
        .split(/[^A-Z0-9]+/)
        .map((c) => c.trim())
        .filter((c) => c.length > 0);

      const groups = [
        ["CLP", "PPR"],
        ["ACS", "TRS", "RAS"],
        ["LS", "KNP", "EMR"],
        ["RCS", "PL", "MM", "SSD"],
        ["NPT", "NT"],
      ];

      const expanded = tokens.flatMap((code) => {
        const match = groups.find((g) => g.includes(code));
        return match ? match : [code];
      });

      return Array.from(new Set(expanded));
    },
    async saveProjection() {
      if (!this.$refs.form || !this.$refs.form.validate()) return;
      this.saving = true;
      this.error = "";
      try {
        const variables = {
          projection: {
            user_id: Number(this.selectedUserId),
            month: this.currentMonthStart.format("YYYY-MM-DD"),
            low_estimate: Number(this.form.low_estimate),
            high_estimate: Number(this.form.high_estimate),
          },
        };
        await this.$apollo.mutate({
          mutation: UpsertFeeProjection,
          variables,
        });
        await this.fetchData();
      } catch (err) {
        this.error = err.message || "Unable to save projection.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.projection-stat {
  background: #f5f5f5;
  border-radius: 6px;
  padding: 12px;
}
</style>
